import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

/**
 * One tooltip for the whole product.
 *
 * Before this, an explanation was either a native `title=` (invisible to keyboard users and to
 * touch) or a hand-rolled hover panel positioned with absolute coordinates (which flipped off the
 * viewport in RTL). Radix opens on focus as well as hover, closes on Escape, and flips its own side
 * when it runs out of room in either direction.
 */

interface TooltipProps {
  // Short sentence in the user's words. Long paragraphs belong on the page, not in a tooltip.
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  // Cap the bubble. Wider than this and it stops reading as a hint.
  contentClassName?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  className,
  contentClassName,
}) => {
  if (!content) return <>{children}</>;

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <span className={cn('inline-flex', className)}>{children}</span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              'z-[60] max-w-xs rounded-lg border border-border bg-card px-3 py-2',
              'text-xs leading-relaxed text-foreground shadow-lg',
              'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
              contentClassName,
            )}
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-card" width={10} height={5} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

interface InfoTooltipProps {
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  // Names the control for screen readers; defaults to a generic "More about this".
  label?: string;
  'data-testid'?: string;
}

/**
 * The label-adjacent form: a small question mark that carries the explanation. It is a real button
 * so it lands in the tab order, which is the half `title=` could never do.
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  side = 'top',
  className,
  label,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip content={content} side={side}>
      <button
        type="button"
        aria-label={label || t('tooltip.moreInfo')}
        data-testid={testId}
        // Explanations must not submit the form they are explaining, and must not trigger the row
        // or card they sit inside either.
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        className={cn(
          'inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground',
          'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          className,
        )}
      >
        <Icon name="HelpCircle" size={14} aria-hidden="true" />
      </button>
    </Tooltip>
  );
};

export default Tooltip;
